<template>
  <div>
    <ErrorDialog modalName="app-error-watch-dialog"
                 dialogMode="alert"
                 @ok-button-click="closeErrorDialog"
                 :errorCode="errorCode"
                 :description="description"
                 @vue:mounted="dialogReady('ErrorDialog')"
                 ref="theErrorDialog">
      <template v-if="displayContactSupportLink" v-slot:contactSupportLink>
        お手数ですが<ContactSupportLinkComponent>お問い合わせフォーム</ContactSupportLinkComponent>よりご連絡ください
      </template>
      <template v-if="displayPasswordResetLink" v-slot:passwordResetLink>
        <RequestPasswordResetLinkComponent @clickLink="closeErrorDialog()"/>
      </template>
    </ErrorDialog>

    <IpAddressBlockingErrorDialog
                @close="closeIpAddressBlockingErrorDialog"
                :ipAddressBlockingError="ipAddressBlockingError"
                @vue:mounted="dialogReady('IpAddressBlockingErrorDialog')"
                />
    <SessionExpiredErrorDialog 
                @close="closeSessionExpiredErrorDialog"
                :sessionExpiredError="sessionExpiredError"
                @vue:mounted="dialogReady('SessionExpiredErrorDialog')"
                />
    <SiteUpdateRequiredErrorDialog 
                @close="closeSiteUpdateRequiredErrorDialog"
                :siteUpdateRequiredError="siteUpdateRequiredError"
                @vue:mounted="dialogReady('SiteUpdateRequiredErrorDialog')"
                />
    <TooManyRequestsErrorDialog 
                @close="tooManyRequestsError = false"
                :tooManyRequestsError="tooManyRequestsError"
                @vue:mounted="dialogReady('TooManyRequestsErrorDialog')"
                />
    <!-- ネットワークエラー -->
    <DialogPanel modal-name="network-error-dialog"
                 main-button-text="OK"
                 @main-button-click="() => { theNetworkErrorDialog?.hide() }"
                 :modalAttrs="{ maxWidth: 640 }"
                 @vue:mounted="dialogReady('DialogPanel')"
                 ref="theNetworkErrorDialog">

      <div class="network-error-dialog-message">
        ネットワークに接続する必要があります<br/>
        インターネットの接続環境をご確認ください
      </div>
    </DialogPanel>

  </div>
</template>

<script lang="ts">
export default {
  name: "AppErrorWatchDialog",
}
</script>

<script setup lang="ts">
  import type{ AppError} from '@/types/appErrorDef'
  import { ref, computed, watch, defineAsyncComponent } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import * as loggers from "@/utils/loggers"
  import RequestPasswordResetLinkComponent from "@/components/cloud/session/RequestPasswordResetLinkComponent.vue"
  import { routeIdOf } from '@/utils/resource_ids'
  import { loginRouteFor } from '@/router/route_utils'
  import { errorDefaultMessage } from '@/utils/errors'
  import { notifyParent as notifyPixParent } from '@/views/externalConnection/pixJoinIframe'
  import { useAppErrorStore } from '@/stores/appError'
  import { useErrorPage } from '@/stores/errorPage'
  import { useWorkerStore } from '@/stores/worker'
  import { useLoginStore } from "@/stores/login"
  import { usePendingRoute } from '@/stores/pendingRoute'

  const router = useRouter()
  const route = useRoute()

  const appErrorStore = useAppErrorStore();
  const errorPageStore = useErrorPage();
  const workerStore = useWorkerStore();
  const loginStore = useLoginStore()

  // components
  const ErrorDialog = defineAsyncComponent(() => import("./ErrorDialog.vue"))
  const SessionExpiredErrorDialog = defineAsyncComponent(() => import("./SessionExpiredErrorDialog.vue"))
  const IpAddressBlockingErrorDialog = defineAsyncComponent(() => import("./IpAddressBlockingErrorDialog.vue"))
  const SiteUpdateRequiredErrorDialog = defineAsyncComponent(() => import("./SiteUpdateRequiredErrorDialog.vue"))
  const TooManyRequestsErrorDialog = defineAsyncComponent(() => import("./TooManyRequestsErrorDialog.vue"))
  const DialogPanel = defineAsyncComponent(() => import("@/components/ui/DialogPanel.vue"))
  const ContactSupportLinkComponent = defineAsyncComponent(() => import("@/components/cloud/support/ContactSupportLinkComponent.vue"))
  // data
  const errorCode = ref('')
  const description = ref('')
  const sessionExpiredError = ref(false)
  const tooManyRequestsError = ref(false)
  const ipAddressBlockingError = ref(false)
  const siteUpdateRequiredError = ref(false)
  const readyDialogs = ref<string[]>([])
  // template ref
  const theNetworkErrorDialog = ref<InstanceType<typeof DialogPanel>>()
  const theErrorDialog = ref<InstanceType<typeof ErrorDialog>>()
  // computed
  const appError = computed(() => {
    return appErrorStore.appError;
  })
  const afterErrorRoute = computed(() => {
    return errorPageStore.afterErrorRoute;
  })
  const displayContactSupportLink = computed(() => {
    // デフォルトのエラーメッセージの場合に、リンクを表示する
    if (description.value === errorDefaultMessage) return true
    // AppErrorでdisplayContactSupportLinkが明示的にtrueの場合もリンクを表示する
    if (appError.value && appError.value.displayContactSupportLink) return true
    return false
  })
  const loginError = ref(false)
  const displayPasswordResetLink = computed(() => {
    // ログイン失敗時のエラーの場合にパスワード再設定リンクを表示
    return loginError.value
  })
  // watch
  watch(
    () => appError.value,
    (theAppError) => {
      if(!theAppError) {
        // エラー初期化された場合は表示しない
        return
      }
      if(theAppError.error){
        console.error("appError caused by", theAppError.error);
      }

      loggers.warn("appError", {
        kCloudAppError: {
          appError: theAppError,
        }
      });

      show(theAppError);

      loggers.warn("errorCode", {
        kCloudAppError: {
          appError: theAppError,
          errorCode: errorCode.value,
        }
      });

      const routeName = router.currentRoute.value.name;
      if (routeName === null) {
        router.push({ path: '/' })
        return
      }
    },
    { deep: true }
  )
  // methods
  const dialogReady = (dialogName: string) => {
    readyDialogs.value.push(dialogName);

    const names = [
      'SiteUpdateRequiredErrorDialog',
      'IpAddressBlockingErrorDialog',
      'SessionExpiredErrorDialog',
      'TooManyRequestsErrorDialog',
      'DialogPanel',
      'ErrorDialog',
    ];
    if(names.every(name => readyDialogs.value.includes(name))){
      errorPageStore.updateErrorWatcherReady(true);
    }
  }

  const isNetworkErrorMessage = (errorMessage: string) => {
    if (errorMessage === (new Error('Network Error')).toString()) return true
    if (errorMessage === "AxiosError: Network Error") return true
    return false
  }

  const show = (appError: AppError) => {
    if (!theErrorDialog.value) return;
    if (appError.noErrorDialog) {
      return;
    }
    // ネットワークエラーの場合
    if (appError.error && isNetworkErrorMessage(appError.error.toString())) {
      theNetworkErrorDialog.value?.show();
      return
    }

    errorCode.value = errorCodeOf(appError.apiId, appError.apiResponseCode, appError.httpStatus, appError.customErrorCode);
    description.value = appError.description as string;

    if(appError.siteUpdateRequired){
      // Webサイト更新が必要な場合のダイアログを開く
      siteUpdateRequiredError.value = true;
      return;
    }

    if(appError.ipAddressBlocking){
      // IPアドレスで制限された場合のダイアログを開く
      ipAddressBlockingError.value = true;
      return;
    }

    if(appError.tooManyRequestsError){
      // アクセス超過エラー
      tooManyRequestsError.value = true;
      return;
    }

    if(!appError.sessionExpired && !appError.changedPermission && !appError.changedPlan){
      // 通常のエラーの場合、エラーダイアログを開く
      loginError.value = appError.loginError || false
      theErrorDialog.value.show()      
      if (appError.error && appError.error.toString().startsWith("DatabaseClosedError")) {
        // IndexedDB Closed エラーの場合
        router.push({ path: '/' })
      }
      return;
    }

    // 通常のエラー以外（セッション期限切れ、権限変更、プラン変更 etcの場合）
    if (router.currentRoute.value.name === 'Login') {
      // ログイン画面の場合は、セッションエラーダイアログを開く
      sessionExpiredError.value = true;
      // ワーカのタスクを停止する
      workerStore.stopWorkerJobs();
      return;
    }

    // 493、494エラーの場合、強制ログアウトする
    if(appError.changedPermission || appError.changedPlan){
      // ワーカのタスクを停止する
      workerStore.stopWorkerJobs();
      
      // ログアウト
      const apiOptions = { noErrorDialog: true, noInitAppError: true }
      loginStore.logoutAccount({ __apiOptions: apiOptions});

      // ログイン画面へ（リダイレクトなし）
      router.replace({ name: 'Login',  query: { error_occurred: "true" }});
      return
    }

    // ログイン画面以外の場合は、ログイン画面に遷移後、セッションエラーダイアログを開く
    const pendingRouteStore = usePendingRoute()
    const loginRoute = loginRouteFor(pendingRouteStore.getPendingRoute(route))
    const toLoginRoute = {
      ...loginRoute ,
      query: {
        ...loginRoute.query,
        error_occurred: "true",
      }
    }

    router.replace(toLoginRoute);
  }
  /**
   * エラーコードを作って返す
   */
  const errorCodeOf = (apiId?: string, apiResponseCode?: number, httpStatus?: number, customErrorCode?: string) => {
    if (!route.name) return '';
    const page = routeIdOf( route.name.toString());
    if(customErrorCode){
      return `${page}-${customErrorCode}`;
    }

    const api = apiId ? `-${apiId}` : '';
    const err = httpStatus ? '000' : (apiResponseCode || '000');

    return `${page}${api}-${err}`;
  }
  const closeErrorDialog = () => {
    if (!theErrorDialog.value) return;
    theErrorDialog.value.hide()

    // PixJoinRequestWatcher 画面の場合、親ウィンドウに完了を通知する
    if (route.name === 'PixJoinRequestWatcher') {
      notifyPixParent()
      return
    }

    // エラー後の遷移先を設定がありの場合
    if(afterErrorRoute.value){
      // エラー後の遷移先へ遷移
      router.replace(afterErrorRoute.value);

      // エラー後の遷移先へ設定の初期化
      errorPageStore.initAfterErrorRoute();
    }
  }
  const closeSessionExpiredErrorDialog = () => {
    sessionExpiredError.value = false;

    const routeName = router.currentRoute.value.name;
    if (routeName !== 'Login') {
      // ログイン画面へ（リダイレクトあり）
      const loginRoute = loginRouteFor(route)
      router.replace(loginRoute);
    }
  }
  // IP制限ダイアログ表示後の処理
  const closeIpAddressBlockingErrorDialog = () => {
    ipAddressBlockingError.value = false;

    const routeName = router.currentRoute.value.name;
    if (routeName !== 'Login') {
      // ログイン画面へ（リダイレクトあり）
      const loginRoute = loginRouteFor(route)
      router.replace(loginRoute);
    }
  }
  const closeSiteUpdateRequiredErrorDialog = () => {
    siteUpdateRequiredError.value = false;

    // ログイン画面（リダイレクトあり）ルート
    const loginRoute = (
      router.currentRoute.value.name !== 'Login'
        ? loginRouteFor(route)
        : null
    );

    // ワーカのタスクを停止する
    workerStore.stopWorkerJobs();
    // ログアウト
    loginStore.logoutAccount({});

    if (loginRoute) {
      // ログイン画面へ（リダイレクトあり）
      router.replace(loginRoute);
    }
  }
</script>
<style lang="scss" scoped>
.network-error-dialog-message {
  text-align: center;
  font-size: 1.3rem;
  margin: 2rem 0;
}
</style>
